import { useState } from "react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { useGetMyReviews } from "@/lib/api-client-react";
import { Loader2, Star, Calendar, MessageSquare, TrendingUp, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";

export default function Reviews() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useGetMyReviews(page, 10);

  const reviewsData = data?.data;
  const items = reviewsData?.items || [];
  const totalCount = reviewsData?.totalCount || 0;
  const averageRating = reviewsData?.averageRating || 0;

  // Calculate rating distribution
  const distribution = [0, 0, 0, 0, 0];
  items.forEach((r: any) => {
    if (r.rating >= 1 && r.rating <= 5) {
      distribution[r.rating - 1]++;
    }
  });

  return (
    <ProviderLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Patient Reviews</h1>
          <p className="text-muted-foreground mt-2">Monitor what patients are saying about your care.</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-6 rounded-2xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <p>Failed to load reviews. Please try again later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Stats Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" /> Overview
                </h3>
                
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                    {averageRating.toFixed(1)} <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                  </div>
                  <p className="text-sm text-muted-foreground">Based on {totalCount} reviews</p>
                </div>

                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = distribution[star - 1] || 0;
                    const percent = items.length > 0 ? (count / items.length) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-3 text-sm">
                        <div className="w-12 flex items-center gap-1 font-medium">
                          {star} <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        </div>
                        <Progress value={percent} className="h-2 flex-1" />
                        <div className="w-8 text-right text-muted-foreground">{count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Reviews List */}
            <div className="lg:col-span-2 space-y-4">
              {items.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 border shadow-sm text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                  <h3 className="text-lg font-medium text-foreground mb-1">No reviews yet</h3>
                  <p className="text-muted-foreground">When patients review your completed appointments, they will appear here.</p>
                </div>
              ) : (
                items.map((review: any) => {
                  const memberName = review.member?.user?.displayName || 'Anonymous Patient';
                  const initials = memberName.substring(0, 2).toUpperCase();

                  return (
                    <div key={review.id} className="bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border border-primary/10">
                            <AvatarFallback className="bg-primary/5 text-primary text-sm font-semibold">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-foreground">{memberName}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {format(new Date(review.createdAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {review.comment && (
                        <p className="text-foreground/80 leading-relaxed text-sm mt-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                          "{review.comment}"
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </ProviderLayout>
  );
}
